import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { DashboardLayout } from "@/components/DashboardLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CookieConsent } from "@/components/CookieConsent";
import Dashboard from "@/pages/Dashboard";
import SendRequest from "@/pages/SendRequest";
import History from "@/pages/History";
import Staff from "@/pages/Staff";
import QRCodes from "@/pages/QRCodes";
import Settings from "@/pages/Settings";
import PublicReview from "@/pages/PublicReview";
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import Features from "@/pages/Features";
import Docs from "@/pages/Docs";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Contact from "@/pages/Contact";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import CheckoutCancelled from "@/pages/CheckoutCancelled";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import AppSumo from "@/pages/AppSumo";
import Unsubscribe from "@/pages/Unsubscribe";
import Leaderboard from "@/pages/Leaderboard";
import NotFound from "@/pages/not-found";

function DashboardRoutes() {
  return (
    <ProtectedRoute>
      <DashboardLayout>
        <Switch>
          <Route path="/dashboard" component={Dashboard} />
          <Route path="/dashboard/leaderboard" component={Leaderboard} />
          <Route path="/dashboard/send" component={SendRequest} />
          <Route path="/dashboard/history" component={History} />
          <Route path="/dashboard/staff" component={Staff} />
          <Route path="/dashboard/qr-codes" component={QRCodes} />
          <Route path="/dashboard/settings" component={Settings} />
          <Route component={NotFound} />
        </Switch>
      </DashboardLayout>
    </ProtectedRoute>
  );
}

function Router() {
  return (
    <Switch>
      {/* Marketing pages */}
      <Route path="/" component={Landing} />
      <Route path="/pricing" component={Pricing} />
      <Route path="/features" component={Features} />
      <Route path="/docs" component={Docs} />
      <Route path="/blog" component={Blog} />
      <Route path="/blog/:slug" component={BlogPost} />
      <Route path="/terms" component={Terms} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/contact" component={Contact} />
      <Route path="/checkout/success" component={CheckoutSuccess} />
      <Route path="/checkout/cancelled" component={CheckoutCancelled} />
      {/* Auth pages */}
      <Route path="/login" component={Login} />
      <Route path="/signup" component={Signup} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/reset-password" component={ResetPassword} />
      <Route path="/auth/callback" component={AuthCallback} />
      {/* AppSumo activation page */}
      <Route path="/appsumo" component={AppSumo} />
      {/* Email unsubscribe page */}
      <Route path="/unsubscribe/:token" component={Unsubscribe} />
      {/* Public review page */}
      <Route path="/r/:businessSlug/:staffSlug?" component={PublicReview} />
      {/* Dashboard routes (protected) */}
      <Route path="/dashboard/:rest*" component={DashboardRoutes} />
      <Route path="/dashboard" component={DashboardRoutes} />
      {/* Fallback */}
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Router />
            <CookieConsent />
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
